/**
 * Firestore path builders for the creator-platform collections. Centralizing
 * them (one-path rule) keeps every repo, the rules fragments, and the functions
 * layer in agreement on exactly where each doc lives. Paths follow
 * docs/CREATOR-PLATFORM.md "Data model".
 */
import { USERS_COLLECTION, type Uid } from '@djgg/core';

/** Top-level collection ids (single source of the string literals). */
export const COLLECTIONS = {
  creations: 'creations',
  slugs: 'slugs',
  buildJobs: 'buildJobs',
  assetGenJobs: 'assetGenJobs',
  auditJobs: 'auditJobs',
  creatorProfiles: 'creatorProfiles',
  platformSettings: 'platformSettings',
  platformAdminSettings: 'platformAdminSettings',
  buildPipelines: 'buildPipelines',
  llmRequests: 'llmRequests',
  actionLog: 'actionLog',
  creatorQuotas: 'creatorQuotas',
  portalSessions: 'portalSessions',
  matchInvites: 'matchInvites',
  userPresence: 'userPresence',
  chatMessages: 'chatMessages',
  editorsPickReviews: 'editorsPickReviews',
  adminStats: 'adminStats',
  qualityRuns: 'qualityRuns',
  qualityTransitions: 'qualityTransitions',
  qualityEvidence: 'qualityEvidence',
  qualityReservations: 'qualityReservations',
  qualityOutbox: 'qualityOutbox',
  qualityManifests: 'qualityManifests',
  qualityPublications: 'qualityPublications',
  failedCandidates: 'failedCandidates',
  candidateEligibility: 'candidateEligibility',
  candidateRechecks: 'candidateRechecks',
  candidateRecheckResults: 'candidateRecheckResults',
  candidateRecheckLimits: 'candidateRecheckLimits',
  candidatePreviewGrants: 'candidatePreviewGrants',
  candidatePreviewSessions: 'candidatePreviewSessions',
  qualityPlatformControl: 'qualityPlatformControl',
  qualityPlatformControlHistory: 'qualityPlatformControlHistory',
  compositionRuntimeControl: 'compositionRuntimeControl',
  composeLibraryEntries: 'composeLibraryEntries',
  componentRegistryV2: 'componentRegistryV2',
  componentRegistryV2History: 'componentRegistryV2History',
  componentPromotionIssuancesV2: 'componentPromotionIssuancesV2',
  compositionBuildsV2: 'compositionBuildsV2',
  compositionCandidatesV2: 'compositionCandidatesV2',
  compositionGameValidationsV2: 'compositionGameValidationsV2',
  compositionPublicationsV2: 'compositionPublicationsV2',
  compositionPrivateActiveV2: 'compositionPrivateActiveV2',
  compositionPublicLaunchesV2: 'compositionPublicLaunchesV2',
  compositionPublicActiveV2: 'compositionPublicActiveV2',
  compositionPublicValidationsV2: 'compositionPublicValidationsV2',
  compositionPublicCompletionsV2: 'compositionPublicCompletionsV2',
  compositionIntentProposalsV2: 'compositionIntentProposalsV2',
  compositionIntentProposalHeadsV2: 'compositionIntentProposalHeadsV2',
  compositionIntentProposalQuotasV2: 'compositionIntentProposalQuotasV2',
  compositionIntentProposalCommandsV2: 'compositionIntentProposalCommandsV2',
  creationFilmRuns: 'creationFilmRuns',
  creationFilmMaterials: 'creationFilmMaterials',
  creationArtifactRuns: 'creationArtifactRuns',
  creationArtifacts: 'creationArtifacts',
  creationArtifactEdges: 'creationArtifactEdges',
  creationArtifactUsages: 'creationArtifactUsages',
  creationProviderDispatches: 'creationProviderDispatches',
  creationImageBudgets: 'creationImageBudgets',
  presentationRepublishRequests: 'presentationRepublishRequests',
} as const;

export const presentationRepublishRequestsCollection = (): string => COLLECTIONS.presentationRepublishRequests;
export const presentationRepublishRequestDoc = (id: string): string =>
  `${presentationRepublishRequestsCollection()}/${id}`;

export const creationFilmRunsCollection = (): string => COLLECTIONS.creationFilmRuns;
export const creationFilmRunDoc = (runId: string): string =>
  `${creationFilmRunsCollection()}/${runId}`;
export const creationFilmMaterialsCollection = (): string => COLLECTIONS.creationFilmMaterials;
const creationFilmPathPart = (value: string): string => encodeURIComponent(value).replace(/~/g, '%7E');
export const creationFilmMaterialDoc = (runId: string, materialId: string): string =>
  `${creationFilmMaterialsCollection()}/${creationFilmPathPart(runId)}~${creationFilmPathPart(materialId)}`;

export const creationArtifactRunsCollection = (): string => COLLECTIONS.creationArtifactRuns;
export const creationArtifactRunDoc = (runId: string): string =>
  `${creationArtifactRunsCollection()}/${encodeURIComponent(runId)}`;
export const creationArtifactsCollection = (): string => COLLECTIONS.creationArtifacts;
export const creationArtifactDoc = (artifactId: string): string =>
  `${creationArtifactsCollection()}/${encodeURIComponent(artifactId)}`;
export const creationArtifactEdgesCollection = (): string => COLLECTIONS.creationArtifactEdges;
export const creationArtifactEdgeDoc = (edgeId: string): string =>
  `${creationArtifactEdgesCollection()}/${encodeURIComponent(edgeId)}`;
export const creationArtifactUsagesCollection = (): string => COLLECTIONS.creationArtifactUsages;
export const creationArtifactUsageDoc = (usageId: string): string =>
  `${creationArtifactUsagesCollection()}/${encodeURIComponent(usageId)}`;
export const creationProviderDispatchesCollection = (): string => COLLECTIONS.creationProviderDispatches;
export const creationProviderDispatchDoc = (dispatchId: string): string =>
  `${creationProviderDispatchesCollection()}/${encodeURIComponent(dispatchId)}`;
export const creationImageBudgetsCollection = (): string => COLLECTIONS.creationImageBudgets;
export const creationImageBudgetDoc = (runId: string): string =>
  `${creationImageBudgetsCollection()}/${encodeURIComponent(runId)}`;

export const compositionIntentProposalDocV2 = (id: string): string => `${COLLECTIONS.compositionIntentProposalsV2}/${id}`;
export const compositionIntentProposalCommandDocV2 = (id: string): string => `${COLLECTIONS.compositionIntentProposalCommandsV2}/${id}`;
export const compositionIntentProposalHeadDocV2 = (creationId: string): string => `${COLLECTIONS.compositionIntentProposalHeadsV2}/${creationId}`;
export const compositionIntentProposalQuotaDocV2 = (uid: string, day: string): string => `${COLLECTIONS.compositionIntentProposalQuotasV2}/${encodeURIComponent(uid)}~${day}`;

export const componentRegistryStateDoc = (): string => `${COLLECTIONS.componentRegistryV2}/global`;
export const componentRegistryHistoryCollection = (): string => COLLECTIONS.componentRegistryV2History;
export const componentRegistryHistoryDoc = (commandId: string): string =>
  `${componentRegistryHistoryCollection()}/${commandId}`;
export const componentPromotionIssuanceDoc = (digest: string): string =>
  `${COLLECTIONS.componentPromotionIssuancesV2}/${digest}`;

/** Subcollection ids under `creations/{id}`. */
export const SUBCOLLECTIONS = {
  versions: 'versions',
  likes: 'likes',
  comments: 'comments',
  feedback: 'feedback',
  viewShards: 'viewShards',
  viewers: 'viewers',
  assetVariants: 'assetVariants',
  assetRejections: 'assetRejections',
} as const;

/** Subcollection ids under `users/{uid}` (the game-platform user root — see
 *  @djgg/core firestorePaths; the badge engine hangs its per-user docs there). */
export const USER_SUBCOLLECTIONS = {
  badges: 'badges',
  gameFinishes: 'gameFinishes',
  gamePlays: 'gamePlays',
  leaderboardFirsts: 'leaderboardFirsts',
  chatPeers: 'chatPeers',
} as const;

/** Collection-GROUP keys for {@link import('@djgg/core').DocStorePort.countGroup}:
 *  the `/`-joined collection-name chain addressing one subcollection across ALL
 *  parents (the badge metrics count per-user across every creation). */
export const GROUPS = {
  likes: `${COLLECTIONS.creations}/${SUBCOLLECTIONS.likes}`,
  comments: `${COLLECTIONS.creations}/${SUBCOLLECTIONS.comments}`,
  versions: `${COLLECTIONS.creations}/${SUBCOLLECTIONS.versions}`,
  assetVariants: `${COLLECTIONS.creations}/${SUBCOLLECTIONS.assetVariants}`,
} as const;

/** The single settings doc id under `platformSettings`. */
export const PLATFORM_SETTINGS_DOC = 'global';
export const PLATFORM_ADMIN_SETTINGS_DOC = 'global';

export const platformAdminSettingsDoc = (): string =>
  `${COLLECTIONS.platformAdminSettings}/${PLATFORM_ADMIN_SETTINGS_DOC}`;

export const creationsCollection = (): string => COLLECTIONS.creations;
export const creationDoc = (creationId: string): string =>
  `${COLLECTIONS.creations}/${creationId}`;

/** Slug claim doc — existence reserves a slug platform-wide. */
export const slugDoc = (slug: string): string => `${COLLECTIONS.slugs}/${slug}`;

export const versionsCollection = (creationId: string): string =>
  `${creationDoc(creationId)}/${SUBCOLLECTIONS.versions}`;
export const versionDoc = (creationId: string, n: number): string =>
  `${versionsCollection(creationId)}/${n}`;

export const likesCollection = (creationId: string): string =>
  `${creationDoc(creationId)}/${SUBCOLLECTIONS.likes}`;
export const likeDoc = (creationId: string, uid: Uid): string =>
  `${likesCollection(creationId)}/${uid}`;

/** `creations/{id}/feedback/{versionN}:{uid}` — one keep/discard verdict per
 *  respondent per played version. `:` is safe: version is a number and uids
 *  never contain it (editorsPickReviews precedent). */
export const feedbackCollection = (creationId: string): string =>
  `${creationDoc(creationId)}/${SUBCOLLECTIONS.feedback}`;
export const feedbackDocId = (versionN: number, uid: Uid): string => `${versionN}:${uid}`;
export const feedbackDoc = (creationId: string, versionN: number, uid: Uid): string =>
  `${feedbackCollection(creationId)}/${feedbackDocId(versionN, uid)}`;

export const commentsCollection = (creationId: string): string =>
  `${creationDoc(creationId)}/${SUBCOLLECTIONS.comments}`;
export const commentDoc = (creationId: string, commentId: string): string =>
  `${commentsCollection(creationId)}/${commentId}`;

export const viewersCollection = (creationId: string): string =>
  `${creationDoc(creationId)}/${SUBCOLLECTIONS.viewers}`;
export const viewerDoc = (creationId: string, uid: string): string =>
  `${viewersCollection(creationId)}/${uid}`;

export const viewShardsCollection = (creationId: string): string =>
  `${creationDoc(creationId)}/${SUBCOLLECTIONS.viewShards}`;
export const viewShardDoc = (creationId: string, shard: number): string =>
  `${viewShardsCollection(creationId)}/${shard}`;

export const buildJobsCollection = (): string => COLLECTIONS.buildJobs;
export const buildJobDoc = (jobId: string): string =>
  `${COLLECTIONS.buildJobs}/${jobId}`;

export const qualityRunsCollection = (): string => COLLECTIONS.qualityRuns;
export const qualityRunDoc = (runId: string): string => `${COLLECTIONS.qualityRuns}/${runId}`;
export const qualityTransitionDoc = (runId: string, eventId: string): string =>
  `${COLLECTIONS.qualityTransitions}/${runId}:${eventId}`;
export const qualityEvidenceDoc = (runId: string, evidenceId: string): string =>
  `${COLLECTIONS.qualityEvidence}/${runId}:${evidenceId}`;
export const qualityReservationDoc = (reservationId: string): string =>
  `${COLLECTIONS.qualityReservations}/${reservationId}`;
export const qualityReservationsCollection = (): string => COLLECTIONS.qualityReservations;
export const qualityOutboxDoc = (outboxId: string): string =>
  `${COLLECTIONS.qualityOutbox}/${outboxId}`;
export const qualityManifestDoc = (runId: string): string =>
  `${COLLECTIONS.qualityManifests}/${runId}`;
export const qualityPublicationDoc = (runId: string): string =>
  `${COLLECTIONS.qualityPublications}/${runId}`;
export const failedCandidateDoc = (candidateId: string): string =>
  `${COLLECTIONS.failedCandidates}/${candidateId}`;
export const candidateEligibilityDoc = (candidateId: string, assessmentId: string): string =>
  `${COLLECTIONS.candidateEligibility}/${candidateId}:${assessmentId}`;
export const candidateEligibilityCollection = (): string => COLLECTIONS.candidateEligibility;
export const candidateRecheckDoc = (jobId: string): string =>
  `${COLLECTIONS.candidateRechecks}/${jobId}`;
export const candidateRecheckResultDoc = (jobId: string): string =>
  `${COLLECTIONS.candidateRecheckResults}/${jobId}`;
export const candidateRecheckLimitDoc = (key: string): string =>
  `${COLLECTIONS.candidateRecheckLimits}/${key}`;
export const candidatePreviewGrantDoc = (tokenHash: string): string =>
  `${COLLECTIONS.candidatePreviewGrants}/${tokenHash}`;
export const candidatePreviewSessionDoc = (sessionHash: string): string =>
  `${COLLECTIONS.candidatePreviewSessions}/${sessionHash}`;
export const qualityPlatformControlDoc = (): string =>
  `${COLLECTIONS.qualityPlatformControl}/global`;
export const qualityPlatformControlHistoryDoc = (commandId: string): string =>
  `${COLLECTIONS.qualityPlatformControlHistory}/${commandId}`;
export const compositionRuntimeControlDoc = (): string =>
  `${COLLECTIONS.compositionRuntimeControl}/global`;
export const composeLibraryEntriesCollection = (): string => COLLECTIONS.composeLibraryEntries;
export const composeLibraryEntryDoc = (digest: string): string => `${COLLECTIONS.composeLibraryEntries}/${digest}`;
export const qualityOutboxCollection = (): string => COLLECTIONS.qualityOutbox;
export const qualityRunsCollectionPath = (): string => COLLECTIONS.qualityRuns;
export const candidateRechecksCollection = (): string => COLLECTIONS.candidateRechecks;

export const assetGenJobsCollection = (): string => COLLECTIONS.assetGenJobs;
export const assetGenJobDoc = (jobId: string): string =>
  `${COLLECTIONS.assetGenJobs}/${jobId}`;

export const auditJobsCollection = (): string => COLLECTIONS.auditJobs;
export const auditJobDoc = (jobId: string): string =>
  `${COLLECTIONS.auditJobs}/${jobId}`;

export const llmRequestsCollection = (): string => COLLECTIONS.llmRequests;
export const llmRequestDoc = (id: string): string =>
  `${COLLECTIONS.llmRequests}/${id}`;

export const editorsPickReviewsCollection = (): string => COLLECTIONS.editorsPickReviews;
/** One review verdict per (creationId, version) — the 2-part key packs into
 *  the doc id `{creationId}:{version}`. Neither part can contain ':' (creation
 *  ids are uuid/`native-<slug>`-shaped, version a number), so the split is
 *  unambiguous — same convention as {@link userGameFinishDoc}. */
export const editorsPickReviewDoc = (creationId: string, version: number): string =>
  `${COLLECTIONS.editorsPickReviews}/${creationId}:${version}`;

export const portalSessionsCollection = (): string => COLLECTIONS.portalSessions;
export const portalSessionDoc = (sessionId: string): string =>
  `${COLLECTIONS.portalSessions}/${sessionId}`;

export const adminStatsCollection = (): string => COLLECTIONS.adminStats;
/** Doc id = the 4h-grid-floored window_start (ms epoch) as a string. */
export const adminStatsDoc = (windowStart: number): string =>
  `${COLLECTIONS.adminStats}/${windowStart}`;

export const actionLogCollection = (): string => COLLECTIONS.actionLog;
export const actionLogDoc = (id: string): string => `${COLLECTIONS.actionLog}/${id}`;

export const matchInvitesCollection = (): string => COLLECTIONS.matchInvites;
export const matchInviteDoc = (token: string): string =>
  `${COLLECTIONS.matchInvites}/${token}`;

export const userPresenceCollection = (): string => COLLECTIONS.userPresence;
/** One presence row per user — the doc id IS the uid. */
export const userPresenceDoc = (uid: Uid): string =>
  `${COLLECTIONS.userPresence}/${uid}`;

/** The Clubhouse is ONE global room, so its messages are a flat top-level
 *  collection — there is no room id to key on (see COLLECTIONS.chatMessages). */
export const chatMessagesCollection = (): string => COLLECTIONS.chatMessages;
export const chatMessageDoc = (messageId: string): string =>
  `${COLLECTIONS.chatMessages}/${messageId}`;

export const creatorProfilesCollection = (): string => COLLECTIONS.creatorProfiles;
export const creatorProfileDoc = (uid: Uid): string =>
  `${COLLECTIONS.creatorProfiles}/${uid}`;

export const platformSettingsDoc = (): string =>
  `${COLLECTIONS.platformSettings}/${PLATFORM_SETTINGS_DOC}`;

export const assetVariantsCollection = (creationId: string): string =>
  `${creationDoc(creationId)}/${SUBCOLLECTIONS.assetVariants}`;
export const assetRejectionDoc = (creationId: string, digest: string): string =>
  `${creationDoc(creationId)}/${SUBCOLLECTIONS.assetRejections}/${digest}`;
export const assetRejectionHistoryDoc = (creationId: string, revision: number): string =>
  `${creationDoc(creationId)}/assetRejectionHistory/${revision}`;
export const assetVariantDoc = (creationId: string, variantId: string): string =>
  `${assetVariantsCollection(creationId)}/${variantId}`;

// ── badge engine: per-user docs under the game-platform `users/{uid}` root ────

export const userBadgesCollection = (uid: Uid): string =>
  `${USERS_COLLECTION}/${uid}/${USER_SUBCOLLECTIONS.badges}`;
export const userBadgeDoc = (uid: Uid, badgeId: string): string =>
  `${userBadgesCollection(uid)}/${badgeId}`;

export const userGameFinishesCollection = (uid: Uid): string =>
  `${USERS_COLLECTION}/${uid}/${USER_SUBCOLLECTIONS.gameFinishes}`;
/** One finish rollup per (gameKey, mode, UTC day) — the 3-part key packs into
 *  the doc id `{gameKey}:{mode}:{day}` (no part can contain ':'). */
export const userGameFinishDoc = (
  uid: Uid,
  gameKey: string,
  mode: 'solo' | 'multi',
  dayIso: string,
): string => `${userGameFinishesCollection(uid)}/${gameKey}:${mode}:${dayIso}`;

export const userGamePlaysCollection = (uid: Uid): string =>
  `${USERS_COLLECTION}/${uid}/${USER_SUBCOLLECTIONS.gamePlays}`;
/** One play-history row per game the user has played — the doc id IS the game
 *  key (creation id or native slug). */
export const userGamePlayDoc = (uid: Uid, gameKey: string): string =>
  `${userGamePlaysCollection(uid)}/${gameKey}`;

export const userLeaderboardFirstsCollection = (uid: Uid): string =>
  `${USERS_COLLECTION}/${uid}/${USER_SUBCOLLECTIONS.leaderboardFirsts}`;
export const userLeaderboardFirstDoc = (uid: Uid, gameId: string): string =>
  `${userLeaderboardFirstsCollection(uid)}/${gameId}`;

export const userChatPeersCollection = (uid: Uid): string =>
  `${USERS_COLLECTION}/${uid}/${USER_SUBCOLLECTIONS.chatPeers}`;
export const userChatPeerDoc = (uid: Uid, peerUid: Uid): string =>
  `${userChatPeersCollection(uid)}/${peerUid}`;

export const creatorQuotasCollection = (): string => COLLECTIONS.creatorQuotas;
/** One doc per (attributed user, UTC day): `creatorQuotas/{uid}_{yyyy-mm-dd}`. */
export const creatorQuotaDoc = (uid: Uid, dayKey: string): string =>
  `${COLLECTIONS.creatorQuotas}/${uid}_${dayKey}`;

/** Versioned experimental build recipes; mutations are server-only. */
export const buildPipelinesDoc = (): string => `${COLLECTIONS.buildPipelines}/catalog`;

/** Private dormant V2 durability records, never public version payloads. */
export const compositionBuildDocV2 = (buildId: string): string => `${COLLECTIONS.compositionBuildsV2}/${buildId}`;
export const compositionCandidateDocV2 = (buildId: string, commandId: string): string =>
  `${COLLECTIONS.compositionCandidatesV2}/${buildId}~${commandId}`;

export const compositionGameValidationDocV2 = (receiptDigest: string): string => `${COLLECTIONS.compositionGameValidationsV2}/${receiptDigest}`;
export const compositionPublicationDocV2 = (buildId: string, commandId: string): string => `${COLLECTIONS.compositionPublicationsV2}/${buildId}~${commandId}`;
export const compositionPrivateActiveDocV2 = (creationId: string): string => `${COLLECTIONS.compositionPrivateActiveV2}/${creationId}`;
export const compositionPublicLaunchDocV2 = (creationId: string, version: number): string =>
  `${COLLECTIONS.compositionPublicLaunchesV2}/${creationId}~${version}`;
export const compositionPublicActiveDocV2 = (creationId: string): string => `${COLLECTIONS.compositionPublicActiveV2}/${creationId}`;
export const compositionPublicValidationDocV2 = (digest: string): string => `${COLLECTIONS.compositionPublicValidationsV2}/${digest}`;
export const compositionPublicCompletionDocV2 = (completionId: string): string =>
  `${COLLECTIONS.compositionPublicCompletionsV2}/${completionId}`;

// Private full-source executable reviews and append-only atomic consumption.
export const compositionIntentReviewDocV2 = (digest: string): string => `compositionIntentReviewsV2/${digest}`;
export const compositionIntentReviewConsumptionDocV2 = (proposalId: string): string => `compositionIntentReviewConsumptionsV2/${proposalId}`;
